import { z } from 'zod'

export enum LEGAL_ENTITY_REGION {
  EU = 'EU',
  NON_EU = 'NON_EU',
}

export enum Salutations {
  EMPTY = '',
  MR = 'Mr.',
  MS = 'Ms.',
  NON_BINARY = 'Non-binary',
}

const BillingAddressSchema = z.object({
  city: z.string(),
  country: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  countryCode: z.string(),
  postalCode: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  state: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  street: z.string(),
  street2: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
})
export type BillingAddress = z.infer<typeof BillingAddressSchema>

const LegalEntityDetailsSchema = z.object({
  region: z
    .nativeEnum(LEGAL_ENTITY_REGION)
    .nullish()
    .transform((data) => data ?? undefined),
  vatID: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  eoriNumber: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  taxID: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
})

const BillingSchema = z.object({
  address: BillingAddressSchema,
  details: LegalEntityDetailsSchema,
})

export const LegalEntitySchema = z.object({
  code: z.string(),
  name: z.string(),
  billing: BillingSchema,
  createdAt: z.coerce.date(),
  notes: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
})

export type LegalEntity = z.infer<typeof LegalEntitySchema>

export const LegalEntitiesSchema = z.object({
  items: z.array(LegalEntitySchema),
  total: z.number(),
})

const BillingContactSchema = z.object({
  salutation: z
    .nativeEnum(Salutations)
    .nullish()
    .transform((data) => data ?? ''),
  firstName: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  lastName: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
})
export type LegalEntities = z.infer<typeof LegalEntitiesSchema>

// This schema is used for Requesting a new legal entity via shipping-core since master-data service doesn't support this feature yet.
// That's the reason for the structure difference from the LegalEntitySchema.
export const RequestLegalEntitySchema = LegalEntityDetailsSchema.pick({ region: true }).extend({
  billingEntityId: z.string(),
  name: z.string(),
  billingContact: BillingContactSchema.nullish().transform((data) => data ?? {}),
  hasVat: z
    .boolean()
    .nullish()
    .transform((data) => data ?? false),
  vatId: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  eoriNumber: z.string(),
  billingAddress: BillingAddressSchema,
  notes: z
    .string()
    .nullish()
    .transform((data) => data ?? ''),
  defaultForIntegrations: z
    .boolean()
    .nullish()
    .transform((data) => data ?? false),
  taxId: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  paCode: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  pecEmailAddress: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})

export type RequestLegalEntitySchemaType = z.infer<typeof RequestLegalEntitySchema>
